import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import config from "../../utils/config";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";
import { MoveDown } from "@mui/icons-material";
import EventPicker from "../EventPicker/EventPicker";

const numRows = 10;

const Wishlist = (props) => {
  const { eventId } = useParams();

  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchDate, setSearchDate] = useState(null);
  const [wishlistEntries, setWishlistEntries] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuEntry, setMenuEntry] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationOnYes, setConfirmationOnYes] = useState(null);
  const [confirmationOnClose, setConfirmationOnClose] = useState(null);
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [pendingTransferId, setPendingTransferId] = useState(null);

  useEffect(() => {
    props.setTitle("View Wishlist");
  }, []); // eslint-disable-line

  useEffect(() => {
    setPage(0);
    if (eventId != null) fetchWishlistEntries(0);
  }, [props.organization, eventId]); // eslint-disable-line

  useEffect(() => {
    onSearchTextDateChanged();
  }, [searchText, searchDate]); // eslint-disable-line

  const onSearchTextDateChanged = useDebouncedCallback(() => {
    setPage(0);
    if (eventId != null) fetchWishlistEntries(0);
  }, 500);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchWishlistEntries(newPage);
  };

  const reloadWishlist = () => {
    setPage(0);
    fetchWishlistEntries(0);
  };

  const fetchNumWishlistEntries = async () => {
    let q = `${config.API_ENDPOINT}/wishlist/${props.organization}/count?eventId=${eventId}`;

    if (searchText !== "") {
      q += `&search=${searchText}`;
    }

    if (searchDate) {
      q += `&searchDate=${searchDate.startOf("day").valueOf()}`;
    }

    axios
      .get(q, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => setTotalRows(res.data.count))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchWishlistEntries = (page) => {
    fetchNumWishlistEntries();

    let q = `${config.API_ENDPOINT}/wishlist/${props.organization}/all?page=${page}&numRows=${numRows}&eventId=${eventId}`;

    if (searchText !== "") {
      q += `&search=${searchText}`;
    }

    if (searchDate) {
      q += `&searchDate=${searchDate.startOf("day").valueOf()}`;
    }

    axios
      .get(q, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        setWishlistEntries(res.data);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const showDeleteDialog = (entryId) => {
    setConfirmationMessage("Are you sure you want to delete this entry?");
    setConfirmationOnClose(() => () => {
      setConfirmationOpen(false);
      setMenuAnchorEl(null);
    });
    setConfirmationOnYes(() => () => {
      setConfirmationOpen(false);
      setMenuAnchorEl(null);
      deleteEntry(entryId);
    });
    setConfirmationOpen(true);
  };

  const deleteEntry = useDebouncedCallback(
    (entryId) => {
      axios
        .delete(`${config.API_ENDPOINT}/wishlist/${props.organization}/${entryId}`, {
          headers: { "x-access-token": localStorage.getItem("access-token") },
        })
        .then(() => {
          toast.success("Success!");
          setMenuAnchorEl(null);
          setConfirmationOpen(false);
          reloadWishlist();
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              toast.warn("You don't have enough permissions to delete this entry");
              setConfirmationOpen(false);
              setMenuAnchorEl(null);
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const transferSingleEntry = useDebouncedCallback(
    (entryId, toEventId, shared) => {
      axios
        .post(
          `${config.API_ENDPOINT}/wishlist/${props.organization}/transfer-single/${entryId}`,
          {
            fromEvent: eventId,
            toEvent: toEventId,
            toShared: shared,
            endpoint: config.WEB_ENDPOINT,
          },
          {
            headers: { "x-access-token": localStorage.getItem("access-token") },
          }
        )
        .then(() => {
          toast.success("Transferred wishlist entry successfully");
          reloadWishlist();
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            case 400:
              toast.error("There are no available seats in the destination event");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const transferEntries = (id, shared) => {
    setTransferDialogOpen(true);
    axios
      .post(
        `${config.API_ENDPOINT}/wishlist/${props.organization}/transfer`,
        { fromEvent: eventId, toEvent: id, toShared: shared, endpoint: config.WEB_ENDPOINT },
        {
          headers: { "x-access-token": localStorage.getItem("access-token") },
        }
      )
      .then((res) => {
        const transferred = res.data.transferred;
        const total = res.data.total;

        if (transferred === total) {
          toast.success("All entries have been transferred successfully");
        } else {
          toast.warn(`${transferred} out of ${total} entries have been transferred`);
        }

        reloadWishlist();
        setTransferDialogOpen(false);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          case 400:
            toast.error("There are no available seats in the destination event");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }

        setTransferDialogOpen(false);
      });
  };

  const hasActiveFilters = searchText !== "" || searchDate != null;

  return (
    <div>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Wishlist</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>Manage student wishlists and transfer entries between events</Typography>
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Filters</Typography>
          {hasActiveFilters && (
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              {searchText && <Chip label={`Search: ${searchText}`} size="small" onDelete={() => setSearchText("")} />}
              {searchDate && <Chip label={`Date: ${searchDate.format("DD/MM/YYYY")}`} size="small" onDelete={() => setSearchDate(null)} />}
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, pt: 2 }}>
          <Box>
            <DatePicker
              label="Search by date"
              inputVariant="outlined"
              required
              format="DD/MM/YYYY"
              value={searchDate}
              onChange={(date) => setSearchDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
          <OutlinedInput
            fullWidth
            name="search"
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by Name/Passport/ESNcard"
            endAdornment={
              <InputAdornment position="end">
                {searchText === "" ? null : (
                  <IconButton onClick={() => setSearchText("")}>
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            }
          />
        </AccordionDetails>
      </Accordion>

      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, p: 2 }}>
            <Button
              variant="contained"
              startIcon={<MoveDown />}
              onClick={() => setEventPickerOpen(true)}
              size="small"
            >
              Transfer
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>VAT</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>ESNcard</TableCell>
                  <TableCell>Settings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wishlistEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>No wishlist entries found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  wishlistEntries.map((entry) => {
                    let eventName = entry.shared ? entry.event?.parentEvent.name : entry.event?.name;

                    return (
                      <TableRow key={entry._id} hover>
                        <TableCell>{eventName ?? "(Event deleted)"}</TableCell>
                        <TableCell>{moment(entry.datetime).format("HH:mm DD/MM/YYYY")}</TableCell>
                        <TableCell>{entry.name}</TableCell>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell>{entry.vat}</TableCell>
                        <TableCell>{entry.phone}</TableCell>
                        <TableCell>{entry.country}</TableCell>
                        <TableCell>{entry.esnCard}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(ev) => {
                              setMenuEntry(entry);
                              setMenuAnchorEl(ev.currentTarget);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[numRows]}
            rowsPerPage={numRows}
            component="div"
            count={totalRows}
            page={page}
            onPageChange={handlePageChange}
          />
          <Menu
            anchorEl={menuAnchorEl}
            open={menuAnchorEl != null}
            keepMounted
            onClose={() => setMenuAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setPendingTransferId(menuEntry?._id);
                setEventPickerOpen(true);
                setMenuAnchorEl(null);
              }}
            >
              Transfer Entry
            </MenuItem>
            <MenuItem onClick={() => showDeleteDialog(menuEntry?._id)}>Delete</MenuItem>
          </Menu>
        </CardContent>
      </Card>
      <ConfirmationDialog
        open={confirmationOpen}
        message={confirmationMessage}
        onYes={confirmationOnYes}
        onClose={confirmationOnClose}
      />
      <Dialog open={transferDialogOpen}>
        <DialogContent>
          <p style={{ textAlign: "center" }}>
            Transferring... This may take a while depending on the size of the event, available
            seats and number of wishlist entries.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        </DialogContent>
      </Dialog>
      <EventPicker
        open={eventPickerOpen}
        onClose={() => {
          setEventPickerOpen(false);
          setPendingTransferId(null);
        }}
        onConfirm={(id, shared) => {
          if (pendingTransferId) {
            transferSingleEntry(pendingTransferId, id, shared);
            setPendingTransferId(null);
          } else {
            transferEntries(id, shared);
          }
          setEventPickerOpen(false);
        }}
        organization={props.organization}
        shared={false}
      />
    </div>
  );
};

export default Wishlist;
